import React, { memo, useRef, useEffect, useLayoutEffect, useCallback, useMemo, useState } from 'react';
import { User, Bell, ArrowDown, ArrowLeft } from 'lucide-react';
import WebSocketService from '../services/websocket';
import { formatarHorario, formatarDataSeparador } from '../utils/dateFormatter';
import ConfirmModal from './ConfirmModal';
import WhatsAppMessage from './WhatsAppMessage';
import MessageStatus from './MessageStatus';
import MediaMessage, { MediaAlbum } from './MediaMessage';

// Cores consistentes por nome de participante
const participantColors = [
  '#25D366', '#34B7F1', '#FF6B6B', '#FFA726', '#AB47BC',
  '#26A69A', '#EF5350', '#42A5F5', '#66BB6A', '#FFA000',
  '#7E57C2', '#EC407A', '#5C6BC0', '#29B6F6', '#D4E157',
];
const getParticipantColor = (name) => {
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return participantColors[Math.abs(hash) % participantColors.length];
};

const GroupSenderHeader = ({ senderName, phone, darkMode }) => {
  const color = getParticipantColor(senderName);
  return (
    <div className="flex items-center gap-1.5 mb-1">
      <span className="text-xs font-bold" style={{ color }}>{senderName}</span>
      {phone && <span className={`text-[10px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{phone}</span>}
    </div>
  );
};

const QuotedMessageBlock = ({ quotedContent, quotedSenderName, darkMode, isOutgoing }) => {
  if (!quotedContent && !quotedSenderName) return null;
  const borderColor = quotedSenderName ? getParticipantColor(quotedSenderName) : (isOutgoing ? '#25D366' : '#34B7F1');
  return (
    <div
      className="mb-1.5 rounded overflow-hidden cursor-pointer"
      style={{
        background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
        borderLeft: `4px solid ${borderColor}`,
        padding: '6px 8px',
        borderRadius: '4px',
      }}
    >
      {quotedSenderName && (
        <span className="text-xs font-bold block" style={{ color: borderColor }}>
          {quotedSenderName}
        </span>
      )}
      <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} line-clamp-2`}>
        {quotedContent || '[Mensagem]'}
      </span>
    </div>
  );
};

const MessageBubble = memo(({ conv, index, darkMode, onReenviar, isGroup, prevConv }) => {
  const albumMessages = conv.albumMessages || null;
  const albumCaption = albumMessages
    ? [...albumMessages].reverse().map(item => {
        const text = item.conteudo || item.mensagem_recebida || item.resposta_enviada || '';
        return text.replace(/^\[(Imagem|Vídeo)\]\s*/, '').trim();
      }).find(Boolean) || ''
    : '';
  // Normalizar: suportar formato novo (direcao/conteudo) e legado (mensagem_recebida/resposta_enviada)
  const isRecebida = conv.direcao === 'recebida' || (conv.mensagem_recebida && !conv.resposta_enviada);
  const isEnviada = conv.direcao === 'enviada' || (conv.resposta_enviada && !conv.mensagem_recebida);
  const textoOriginal = albumMessages ? albumCaption : (conv.conteudo || conv.mensagem_recebida || conv.resposta_enviada || '');

  // Nome do remetente: campo sender_name ou fallback do prefixo "Nome: msg"
  let senderName = conv.sender_name || null;
  let messageText = textoOriginal;
  if (isGroup && isRecebida && !senderName && textoOriginal) {
    const colonIdx = textoOriginal.indexOf(': ');
    if (colonIdx > 0 && colonIdx < 30) {
      senderName = textoOriginal.substring(0, colonIdx);
      messageText = textoOriginal.substring(colonIdx + 2);
    }
  }

  // Verificar se o remetente anterior é o mesmo
  const prevTexto = prevConv?.conteudo || prevConv?.mensagem_recebida || '';
  let prevSender = prevConv?.sender_name || null;
  if (isGroup && !prevSender && prevTexto) {
    const ci = prevTexto.indexOf(': ');
    if (ci > 0 && ci < 30) prevSender = prevTexto.substring(0, ci);
  }
  const showSender = senderName && senderName !== prevSender;

  return (
  <div key={`msg-${conv.id}-${index}`} className="flex flex-col space-y-1.5 text-[0.85rem]">
    {/* Mensagem Recebida */}
    {isRecebida && (
      <div className="flex justify-start">
        {isGroup && showSender && (
          <div className="flex-shrink-0 mr-2 mt-1">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
              style={{ background: getParticipantColor(senderName) }}>
              {senderName?.charAt(0)?.toUpperCase() || '?'}
            </div>
          </div>
        )}
        {isGroup && senderName && !showSender && <div className="w-8 mr-2 flex-shrink-0" />}
        <div className="max-w-[85%] sm:max-w-sm lg:max-w-lg">
          <div className={`rounded-lg p-2 ${
            darkMode
              ? 'bg-zinc-700 border border-gray-600'
              : 'bg-white border border-gray-200 shadow-sm'
          }`}>
            {isGroup && showSender && <GroupSenderHeader senderName={senderName} phone={conv.participant_phone} darkMode={darkMode} />}
            {(conv.quoted_content || conv.quoted_sender_name) && (
              <QuotedMessageBlock quotedContent={conv.quoted_content} quotedSenderName={conv.quoted_sender_name} darkMode={darkMode} isOutgoing={false} />
            )}
            {albumMessages?.length > 1 ? (
              <MediaAlbum messages={albumMessages} darkMode={darkMode} isOutgoing={false} />
            ) : conv.media_url && conv.message_type !== 'text' ? (
              <MediaMessage conversaId={conv.id} messageType={conv.message_type} darkMode={darkMode} isOutgoing={false} displayText={messageText}
                mediaWidth={conv.media_width} mediaHeight={conv.media_height}
                timestamp={conv.message_type === 'audio' ? formatarHorario(conv.create_at) : undefined} />
            ) : null}
            {(albumMessages ? !!albumCaption : (!conv.media_url || !/^\[(Imagem|Áudio|Vídeo|Documento|Post|Reel|Story)\]/s.test(textoOriginal))) && (
              <WhatsAppMessage 
                text={messageText}
                className={darkMode ? 'text-gray-200' : 'text-gray-800'}
                hidePreview={!!conv.media_url}
              />
            )}
            {conv.message_type !== 'audio' && (
            <div className="flex items-center justify-end mt-1">
              <span className={`text-xs ${
                darkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                {formatarHorario(conv.create_at)}
              </span>
            </div>
            )}
          </div>
        </div>
      </div>
    )}

    {/* Mensagem Enviada */}
    {isEnviada && (
      <div className="flex justify-end">
        <div className="max-w-[85%] sm:max-w-sm lg:max-w-lg">
          {conv.respondida_por_humano && conv.atendente_nome && (
            <p className={`text-xs text-right mb-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {conv.atendente_nome}
            </p>
          )}
          <div className={`rounded-lg p-2 text-white ${
            conv.respondida_por_humano 
              ? 'bg-green-600'
              : conv.respondida_por_ia
              ? darkMode ? 'bg-purple-700' : 'bg-purple-600'
              : darkMode ? 'bg-gray-700' : 'bg-gray-600'
          } ${['failed','erro'].includes(conv.status_mensagem) ? 'opacity-50' : ''}`}>
            {(conv.quoted_content || conv.quoted_sender_name) && (
              <QuotedMessageBlock quotedContent={conv.quoted_content} quotedSenderName={conv.quoted_sender_name} darkMode={darkMode} isOutgoing={true} />
            )}
            {albumMessages?.length > 1 ? (
              <MediaAlbum messages={albumMessages} darkMode={darkMode} isOutgoing={true} />
            ) : conv.media_url && conv.message_type !== 'text' ? (
              <MediaMessage conversaId={conv.id} messageType={conv.message_type} darkMode={darkMode} isOutgoing={true} displayText={textoOriginal}
                mediaWidth={conv.media_width} mediaHeight={conv.media_height}
                timestamp={conv.message_type === 'audio' ? formatarHorario(conv.create_at) : undefined} />
            ) : null}
            {(albumMessages ? !!albumCaption : (!conv.media_url || (textoOriginal && !/^\[(Imagem|Áudio|Vídeo|Documento|Post|Reel|Story)\]/s.test(textoOriginal)))) && (
              <WhatsAppMessage text={textoOriginal} className="mb-1" hidePreview={!!conv.media_url} />
            )}
            <div className="flex items-center justify-end gap-2 text-xs opacity-75">
              <span>{formatarHorario(conv.create_at)}</span>
              <span>{conv.respondida_por_humano ? '👤' : '🤖'}</span>
              <MessageStatus status={conv.status_mensagem} />
            </div>
          </div>
          {['failed','erro'].includes(conv.status_mensagem) && (
            <button
              onClick={() => onReenviar?.(conv)}
              className="text-[10px] text-red-400 hover:underline mt-0.5 ml-auto block"
            >
              Reenviar
            </button>
          )}
        </div>
      </div>
    )}
  </div>
  );
});

const parseMessageTimestamp = (value) => {
  const str = String(value || '');
  const withTZ = /[Zz]$|[+-]\d{2}:\d{2}$/.test(str) ? str : str.replace(' ', 'T') + 'Z';
  return new Date(withTZ).getTime();
};

const isAlbumCandidate = (msg) => {
  const text = msg.conteudo || msg.mensagem_recebida || msg.resposta_enviada || '';
  return !!msg.media_url
    && ['image', 'video'].includes(msg.message_type)
    && !/^\[(Post|Reel|Story)\]/.test(text);
};

const mediaSenderKey = (msg) => {
  const direction = msg.direcao || (msg.resposta_enviada ? 'enviada' : 'recebida');
  return `${direction}:${msg.participant_phone || msg.sender_name || msg.telefone || ''}`;
};

const agruparMidiasConsecutivas = (mensagens) => {
  const result = [];
  let sequence = [];

  const flush = () => {
    if (sequence.length > 1) {
      const latest = sequence[sequence.length - 1];
      result.push({ ...latest, albumMessages: sequence });
    } else if (sequence.length === 1) {
      result.push(sequence[0]);
    }
    sequence = [];
  };

  mensagens.forEach(msg => {
    if (!isAlbumCandidate(msg)) {
      flush();
      result.push(msg);
      return;
    }

    const previous = sequence[sequence.length - 1];
    const gap = previous ? parseMessageTimestamp(msg.create_at) - parseMessageTimestamp(previous.create_at) : 0;
    const belongsToSequence = previous
      && mediaSenderKey(previous) === mediaSenderKey(msg)
      && gap >= 0
      && gap <= 5000;

    if (!previous || belongsToSequence) {
      sequence.push(msg);
    } else {
      flush();
      sequence.push(msg);
    }
  });

  flush();
  return result;
};

const agruparMensagensPorData = (mensagens) => {
  const grupos = {};
  
  mensagens.forEach(msg => {
    const str = String(msg.create_at || '');
    // Assume UTC se não tem timezone info
    const withTZ = str.match(/[Zz]$/) || str.match(/[+-]\d{2}:\d{2}$/) ? str : str.replace(' ', 'T') + 'Z';
    const data = new Date(withTZ);
    if (isNaN(data.getTime())) return;
    // Agrupar pelo dia em BRT (America/Sao_Paulo)
    const dataKey = data.toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' }); // formato YYYY-MM-DD
    
    if (!grupos[dataKey]) {
      grupos[dataKey] = [];
    }
    grupos[dataKey].push(msg);
  });
  
  return grupos;
};

const MessageList = ({ 
  contatoAtivo,
  onBack, 
  mensagens, 
  carregando, 
  onLoadMore, 
  novasMensagens, 
  mostrarNotificacao,
  onClearNotification,
  onIaStatusChange,
  iaPausadaProp,
  onReenviar,
  darkMode,
  scrollToBottomRef,
  onToggleSidebar,
  sidebarAberta,
}) => {
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const observerRef = useRef(null);
  const [mostrarMensagens, setMostrarMensagens] = useState(false);

  // Agrupar dentro do JSX refazia duas varreduras da lista inteira em todo
  // render — inclusive nos que não mexem em `mensagens`.
  const diasAgrupados = useMemo(
    () =>
      Object.entries(agruparMensagensPorData(mensagens)).map(([data, doDia]) => [
        data,
        agruparMidiasConsecutivas(doDia),
      ]),
    [mensagens],
  );
  const prevContatoRef = useRef(null);
  const prevMensagensLenRef = useRef(0);
  const initialScrollPendingRef = useRef(true);
  const isNearBottomRef = useRef(true);
  const loadingMoreRef = useRef(false);
  const anchorRef = useRef(null);

  // Ao trocar de contato, somente a primeira página deve rolar ao final
  useEffect(() => {
    const key = contatoAtivo?.telefone + '_' + contatoAtivo?.instancia_id;
    if (contatoAtivo && key !== prevContatoRef.current) {
      prevContatoRef.current = key;
      prevMensagensLenRef.current = 0;
      initialScrollPendingRef.current = true;
      loadingMoreRef.current = false;
      anchorRef.current = null;
      isNearBottomRef.current = true;
      setMostrarMensagens(true);
    }
  }, [contatoAtivo]);

  // Expor função de scroll para o pai
  useEffect(() => {
    if (scrollToBottomRef) {
      scrollToBottomRef.current = () => {
        messagesContainerRef.current?.scrollTo({
          top: messagesContainerRef.current.scrollHeight,
          behavior: 'smooth'
        });
      };
    }
  }, [scrollToBottomRef]);
  const iaPausada = iaPausadaProp;
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const handleScroll = useCallback((e) => {
    const container = e.currentTarget;
    isNearBottomRef.current = container.scrollHeight - container.scrollTop - container.clientHeight < 120;

    // WhatsApp Web antecipa a carga; não espera o usuário bater no topo.
    if (container.scrollTop > 500 || loadingMoreRef.current) return;

    const containerTop = container.getBoundingClientRect().top;
    const firstVisible = Array.from(container.querySelectorAll('[data-message-id]'))
      .find(element => element.getBoundingClientRect().bottom > containerTop);

    anchorRef.current = firstVisible ? {
      id: firstVisible.getAttribute('data-message-id'),
      offset: firstVisible.getBoundingClientRect().top - containerTop,
    } : null;

    const started = onLoadMore();
    if (started) {
      loadingMoreRef.current = true;
      console.log(`📜 [scroll] Prefetch iniciado; anchor=${anchorRef.current?.id || 'none'}`);
    }
  }, [onLoadMore]);

  const scrollToBottom = useCallback((behavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  }, []);

  // Uma única autoridade de scroll: carga inicial, prepend ancorado ou nova mensagem.
  useLayoutEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || !mostrarMensagens || mensagens.length === 0) return;

    if (initialScrollPendingRef.current) {
      container.scrollTop = container.scrollHeight;
      initialScrollPendingRef.current = false;
      isNearBottomRef.current = true;
    } else if (loadingMoreRef.current && mensagens.length > prevMensagensLenRef.current) {
      const anchor = anchorRef.current;
      const anchorElement = anchor?.id
        ? container.querySelector(`[data-message-id="${CSS.escape(anchor.id)}"]`)
        : null;

      if (anchorElement) {
        const newOffset = anchorElement.getBoundingClientRect().top - container.getBoundingClientRect().top;
        container.scrollTop += newOffset - anchor.offset;
      }

      console.log(`📜 [scroll] Histórico anexado: +${mensagens.length - prevMensagensLenRef.current}; âncora preservada`);
      loadingMoreRef.current = false;
      anchorRef.current = null;
    } else if (mensagens.length > prevMensagensLenRef.current && isNearBottomRef.current) {
      container.scrollTop = container.scrollHeight;
    }

    prevMensagensLenRef.current = mensagens.length;
  }, [mensagens, mostrarMensagens]);

  useEffect(() => {
    if (!carregando && loadingMoreRef.current && mensagens.length === prevMensagensLenRef.current) {
      console.log('📜 [scroll] Carga concluída sem novas mensagens; liberando âncora');
      loadingMoreRef.current = false;
      anchorRef.current = null;
    }
  }, [carregando, mensagens.length]);

  // Observer para notificações
  useEffect(() => {
    if (messagesEndRef.current) {
      observerRef.current = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && mostrarNotificacao) {
            onClearNotification();
          }
        },
        { threshold: 0.1 }
      );
      
      observerRef.current.observe(messagesEndRef.current);
    }
    
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [mostrarNotificacao, onClearNotification]);

  // Listeners para status da IA
  useEffect(() => {
    if (WebSocketService.atendimentoSocket) {
      const handleIaPausada = (data) => {
        console.log('🔴 IA pausada para:', data.telefone, 'contatoAtivo:', contatoAtivo?.telefone);
        if (contatoAtivo && data.telefone === contatoAtivo.telefone) {
          onIaStatusChange?.(true);
        }
      };
      
      const handleIaReativada = (data) => {
        console.log('🟢 IA reativada para:', data.telefone);
        if (contatoAtivo && data.telefone === contatoAtivo.telefone) {
          onIaStatusChange?.(false);
        }
      };
      
      WebSocketService.atendimentoSocket.on('ia_pausada', handleIaPausada);
      WebSocketService.atendimentoSocket.on('ia_reativada', handleIaReativada);
    }
    
    return () => {
      if (WebSocketService.atendimentoSocket) {
        WebSocketService.atendimentoSocket.off('ia_pausada');
        WebSocketService.atendimentoSocket.off('ia_reativada');
      }
    };
  }, [contatoAtivo, onIaStatusChange]);

  // Atualizar estado da IA ao trocar contato
  useEffect(() => {
    if (contatoAtivo && WebSocketService.isAtendimentoConnected) {
      // Verificar status da IA para este contato
      WebSocketService.verificarStatusIA({ telefone: contatoAtivo.telefone });
    } else {
      onIaStatusChange?.(false);
    }
  }, [contatoAtivo]);

  if (!contatoAtivo) {
    return (
      <div className={`flex-1 flex items-center justify-center ${
        darkMode ? 'bg-zinc-900' : 'bg-gray-50'
      }`}>
        <div className={`text-center ${
          darkMode ? 'text-gray-400' : 'text-gray-500'
        }`}>
          <div className={`w-24 h-24 mx-auto mb-4 ${
            darkMode ? 'text-gray-600' : 'text-gray-300'
          }`}>
            💬
          </div>
          <h2 className={`text-xl font-medium mb-2 ${
            darkMode ? 'text-white' : 'text-gray-900'
          }`}>
            Bem-vindo ao Atendimento
          </h2>
          <p className={darkMode ? 'text-gray-500' : 'text-gray-400'}>
            Selecione uma conversa para começar
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
      {/* Header do Chat */}
      <div className={`shrink-0 border-b md:border-l p-3 sm:p-4 ${
        darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-gray-50 border-gray-200'
      }`}>
        <div className="flex items-center space-x-2 sm:space-x-3">
          {onBack && (
            <button className="md:hidden p-1 -ml-1" onClick={onBack}>
              <ArrowLeft className={`w-5 h-5 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} />
            </button>
          )}
          <div className="relative">
            {contatoAtivo.foto_contato ? (
              <img src={contatoAtivo.foto_contato} alt="" className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover"
                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
            ) : null}
            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full items-center justify-center ${
              darkMode ? 'bg-zinc-600' : 'bg-gray-300'
            }`} style={{ display: contatoAtivo.foto_contato ? 'none' : 'flex' }}>
              <User className={`w-4 h-4 sm:w-5 sm:h-5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
            </div>
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1 sm:gap-2">
              <h2 className={`font-semibold truncate text-sm sm:text-base ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {contatoAtivo.nome_contato}
              </h2>
              {contatoAtivo.instancia && (
                <span className={`hidden sm:inline-block text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${
                  darkMode ? 'bg-zinc-600 text-gray-300' : 'bg-gray-200 text-gray-600'
                }`} title={contatoAtivo.instancia.numero_whatsapp || contatoAtivo.instancia.instagram_username || ''}>
                  {contatoAtivo.instancia.platform === 'instagram'
                    ? `@${contatoAtivo.instancia.instagram_username || contatoAtivo.instancia.nome_instancia}`
                    : contatoAtivo.instancia.nome_instancia}
                </span>
              )}
            </div>
            <p className={`hidden sm:block text-sm ${
              darkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>
              {contatoAtivo.instancia?.platform !== 'instagram' && /^\+?\d{7,15}$/.test(contatoAtivo.telefone) ? contatoAtivo.telefone : ''}
            </p>
          </div>
          
          <div className="text-right flex items-center space-x-1 sm:space-x-2">
            {iaPausada && (
              <button
                onClick={() => setShowConfirmModal(true)}
                className={`text-xs px-3 py-1 rounded font-medium ${
                  darkMode
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-blue-500 hover:bg-blue-600 text-white'
                }`}
                title="Finalizar atendimento, remover tags e reativar IA"
              >
                <span className="hidden sm:inline">Finalizar Atendimento</span>
                <span className="sm:hidden">Finalizar</span>
              </button>
            )}
            {iaPausada ? (
              <button
                onClick={() => {
                  if (WebSocketService.isAtendimentoConnected) {
                    console.log('🟢 Clicou em Ativar IA');
                    WebSocketService.reativarIA({ telefone: contatoAtivo.telefone });
                  }
                }}
                className={`text-xs px-2 py-1 rounded ${
                  darkMode
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-green-500 hover:bg-green-600 text-white'
                }`}
              >
                Ativar IA
              </button>
            ) : (
              <button
                onClick={() => {
                  if (WebSocketService.isAtendimentoConnected) {
                    console.log('🔴 Clicou em Pausar IA');
                    WebSocketService.pausarIA({ telefone: contatoAtivo.telefone });
                  }
                }}
                className={`text-xs px-2 py-1 rounded ${
                  darkMode
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-red-500 hover:bg-red-600 text-white'
                }`}
              >
                Pausar IA
              </button>
            )}
            <span className={`hidden sm:inline text-xs font-medium ${
              darkMode ? 'text-green-400' : 'text-green-600'
            }`}>
              Online
            </span>
            {onToggleSidebar && (
              <button onClick={onToggleSidebar} title="Ver perfil do contato"
                className={`p-1.5 rounded-lg transition ${
                  sidebarAberta
                    ? 'bg-nomoz-orange text-white'
                    : darkMode ? 'hover:bg-zinc-700 text-zinc-400' : 'hover:bg-gray-200 text-gray-500'
                }`}>
                <User className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Área de Mensagens */}
      <div 
        ref={messagesContainerRef}
        data-messages-container
        className="flex-1 overflow-y-auto p-2 sm:p-4"
        onScroll={handleScroll}
        style={{
          backgroundImage: darkMode 
            ? `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23252525' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
            : `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23f0f0f0' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          backgroundColor: darkMode ? '#18181b' : '#ffffff',
          overflowAnchor: 'none'
        }}
      >
        {!mostrarMensagens && contatoAtivo && (
          <div className="flex items-center justify-center h-full">
            <div className={`animate-spin rounded-full h-5 w-5 border-2 border-t-transparent ${
              darkMode ? 'border-gray-500' : 'border-gray-400'
            }`}></div>
          </div>
        )}

        {carregando && mensagens.length > 0 && mostrarMensagens && (
          <div className="sticky top-2 z-10 flex justify-center h-0 overflow-visible pointer-events-none">
            <div className={`inline-flex items-center px-4 py-2 rounded-full shadow-sm ${
              darkMode ? 'bg-zinc-700' : 'bg-gray-100'
            }`}>
              <div className={`animate-spin rounded-full h-4 w-4 border-b-2 ${
                darkMode ? 'border-gray-400' : 'border-gray-600'
              } mr-2`}></div>
              <span className={`text-sm ${
                darkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                Carregando mensagens...
              </span>
            </div>
          </div>
        )}
        
        {mostrarMensagens && diasAgrupados.map(([data, mensagensDoDia], dataIndex) => (
          <div key={`${data}-${dataIndex}`} className="space-y-2">
            <div className="flex items-center justify-center my-4">
              <div className={`text-xs px-3 py-1 rounded-full ${
                darkMode
                  ? 'bg-zinc-700 text-gray-300'
                  : 'bg-gray-200 text-gray-600'
              }`}>
                {formatarDataSeparador(data)}
              </div>
            </div>
            
            {mensagensDoDia.map((conv, index, groupedMessages) => (
              <div key={`${data}-${conv.id}-${index}`} data-message-id={String(conv.id || `${conv.create_at}-${index}`)}>
                <MessageBubble 
                  conv={conv} 
                  index={index} 
                  darkMode={darkMode}
                  onReenviar={onReenviar}
                  isGroup={!!contatoAtivo?.is_group || contatoAtivo?.telefone?.includes('group')}
                  prevConv={index > 0 ? groupedMessages[index - 1] : null}
                />
              </div>
            ))}
          </div>
        ))}
        
        <div ref={messagesEndRef} />
      </div>
      
      {/* Notificação de Novas Mensagens */}
      {mostrarNotificacao && novasMensagens > 0 && (
        <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 z-50">
          <button
            onClick={() => {
              scrollToBottom();
              onClearNotification();
            }}
            className={`px-4 py-2 rounded-full shadow-lg flex items-center space-x-2 transition-all ${
              darkMode
                ? 'bg-green-600 hover:bg-green-700 text-white'
                : 'bg-green-500 hover:bg-green-600 text-white'
            }`}
          >
            <span className="text-sm font-medium">
              {novasMensagens} nova{novasMensagens > 1 ? 's' : ''} mensagem{novasMensagens > 1 ? 's' : ''}
            </span>
            <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
          </button>
        </div>
      )}
      
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={() => {
          if (WebSocketService.isAtendimentoConnected) {
            console.log('✅ Finalizando atendimento');
            WebSocketService.finalizarAtendimento({ telefone: contatoAtivo.telefone, instanciaId: contatoAtivo.instancia_id });
            onIaStatusChange?.(false);
            setShowConfirmModal(false);
          }
        }}
        title="Finalizar Atendimento"
        message="Deseja finalizar o atendimento? As tags serão removidas e a IA será reativada."
        confirmText="Finalizar"
        cancelText="Cancelar"
        darkMode={darkMode}
      />
    </div>
  );
};

export default memo(MessageList);